<mat-card fxFlex class="card-table has-progress-bar">
  <mat-progress-bar [mode]="(progress$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>

  <mat-card-title *ngIf="!readOnly && !embedded" fxLayout="row" fxLayoutAlign="space-between center">
    <span>{{ 'patient-details.insurance-card.title' | translate }}</span>
    <button
      *appHasPermission="'patient-insurance.edit'"
      mat-stroked-button
      (click)="onAddInsuranceClick()"
    >
      <mat-icon>add</mat-icon>
      <span>{{ 'patient-details.insurance-card.button-add' | translate }}</span>
    </button>
  </mat-card-title>

  <ng-template #carrierHeader>{{ 'patient-details.insurance-card.table.carrier' | translate }}</ng-template>
  <ng-template #carrierCell let-insurance>
    {{ insurance.customCarrierName || insurance.clinicCarrierName || insurance.clinicCarrierId }}
  </ng-template>

  <ng-template #statusHeader>{{ 'patient-details.insurance-card.table.status' | translate }}</ng-template>
  <ng-template #statusCell let-insurance>
    <ng-container *ngIf="isSelfPayLikeInsurance(insurance); else insuranceInfo">
      {{ 'common.not-required' | translate }}
    </ng-container>
    <ng-template #insuranceInfo>
      <ng-container [ngSwitch]="insurance.status">
        <div *ngSwitchCase="PatientInsuranceStatus.Active" fxLayout="column">
          <span>{{ 'common.active' | translate }}</span>
          <small *ngIf="insurance.lastVerifiedAt">
            Last Verified {{ insurance.lastVerifiedAt | momentDate: 'local' }}
            <ng-container *ngIf="insurance.lastVerifiedBy">
              by {{ insurance.lastVerifiedBy }}
            </ng-container>
          </small>
        </div>
        <span *ngSwitchCase="PatientInsuranceStatus.Inactive">{{ 'common.inactive' | translate }}</span>
        <span *ngSwitchDefault>{{ 'dialog.insurance.not-verified' | translate }}</span>
      </ng-container>
    </ng-template>
  </ng-template>

  <ng-template #memberHeader>{{ 'patient-details.insurance-card.table.member' | translate }}</ng-template>
  <ng-template #memberCell let-insurance>
    <div fxLayout="column">
      <mmx-copy-text>{{ insurance.memberId || '' }}</mmx-copy-text>
      <ng-container *ngIf="insurance.groupNumber">
        <div class="text-sm">{{ insurance.groupNumber }}</div>
      </ng-container>
    </div>
  </ng-template>

  <ng-template #typeHeader>{{ 'patient-details.insurance-card.table.type' | translate }}</ng-template>
  <ng-template #typeCell let-insurance let-displayType="displayType">
    {{ 'insurances.type.' + (displayType ?? insurance.type) | translate }}
  </ng-template>

  <mat-card-content fxLayout="column">
    <mat-button-toggle-group
      *ngIf="showSegmentToggle"
      class="insurances-card__segment-toggle"
      [value]="selectedSegment"
      (change)="selectedSegment = $event.value"
      data-insurances-card-segment-toggle
    >
      <mat-button-toggle value="thisVisit">
        {{ (isMultiAccountScope ? 'patient-details.insurance-card.selected-visits' : 'patient-details.insurance-card.this-visit') | translate }} ({{ dataSource.data.length }})
      </mat-button-toggle>
      <mat-button-toggle value="other">
        {{ 'patient-details.insurance-card.other-insurances' | translate: { count: otherDataSource.data.length } }}
      </mat-button-toggle>
    </mat-button-toggle-group>
    <p
      *ngIf="selectedSegment === 'thisVisit' && dataSource.data.length === 0 && otherDataSource.data.length > 0"
      class="insurances-card__empty pb-3"
      data-insurances-card-empty-this-visit
    >
      {{ (isMultiAccountScope ? 'patient-details.insurance-card.empty-selected-visits' : 'patient-details.insurance-card.empty-this-visit') | translate }}
    </p>
    <table
      mat-table
      [dataSource]="selectedSegment === 'other' ? otherDataSource : dataSource"
      *ngIf="!(selectedSegment === 'thisVisit' && dataSource.data.length === 0 && otherDataSource.data.length > 0)"
    >
      <ng-container matColumnDef="carrier">
        <th mat-header-cell *matHeaderCellDef><ng-container *ngTemplateOutlet="carrierHeader"></ng-container></th>
        <td mat-cell *matCellDef="let insurance"><ng-container *ngTemplateOutlet="carrierCell; context: { $implicit: insurance }"></ng-container></td>
      </ng-container>
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef><ng-container *ngTemplateOutlet="statusHeader"></ng-container></th>
        <td mat-cell *matCellDef="let insurance"><ng-container *ngTemplateOutlet="statusCell; context: { $implicit: insurance }"></ng-container></td>
      </ng-container>
      <ng-container matColumnDef="member">
        <th mat-header-cell *matHeaderCellDef><ng-container *ngTemplateOutlet="memberHeader"></ng-container></th>
        <td mat-cell *matCellDef="let insurance"><ng-container *ngTemplateOutlet="memberCell; context: { $implicit: insurance }"></ng-container></td>
      </ng-container>
      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef><ng-container *ngTemplateOutlet="typeHeader"></ng-container></th>
        <td mat-cell *matCellDef="let insurance"><ng-container *ngTemplateOutlet="typeCell; context: { $implicit: insurance, displayType: selectedSegment === 'thisVisit' ? resolveThisVisitType(insurance) : undefined }"></ng-container></td>
      </ng-container>
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'patient-details.insurance-card.table.actions' | translate }}
        </th>
        <td mat-cell *matCellDef="let insurance">
          <button mat-icon-button [matMenuTriggerFor]="options">
            <mat-icon>more_vert</mat-icon>
          </button>

          <mat-menu #options="matMenu">
            <button
              *appHasPermission="'patient-insurance.edit'"
              mat-menu-item
              type="button"
              (click)="onEditInsuranceClick(insurance)"
            >
              <mat-icon>edit</mat-icon>
              {{ 'common.edit' | translate }}
            </button>

            <ng-container *ngIf="!insurance.lookupType || insurance.lookupType === INSURANCE_PROVIDER_LOOKUP_TYPE.MEDICAL_INSURANCE">
              <button
                *appHasPermission="'patient.eligibility.edit'"
                mat-menu-item
                type="button"
                (click)="onPullEligibilityClick(insurance)"
                [color]="insurance.carrierId ? undefined : 'warn'"
              >
                <mat-icon>policy</mat-icon>
                {{ 'patient-details.eligibility-card.button-pull-eb' | translate }}
              </button>
            </ng-container>

            <ng-container *ngIf="insurance.eligibilityId && !embedded">
              <a *appHasPermission="'patient.eligibility.view'"
                mat-menu-item
                [routerLink]="['/patient', insurance.patientId, 'appointment', appointment?.id, 'eligibility', insurance.eligibilityId]"
                target='_blank'>
                <mat-icon>shield</mat-icon>
                {{ 'patient-details.eligibility-card.view-eb' | translate }}
              </a>

              <!-- <button mat-menu-item (click)="openRawResponse()">
                <mat-icon>code</mat-icon>
                <span>{{
                  'patient-details.eligibility-card.view-raw-response' | translate
                  }}</span>
              </button> -->
            </ng-container>

            <button mat-menu-item type="button" (click)="openInsuranceHistoryDialog(insurance)">
              <mat-icon>history</mat-icon>
              History
            </button>

            <ng-container *appHasPermission="'patient-insurance.delete'">
              <mat-divider></mat-divider>

              <button mat-menu-item (click)="deleteInsurance(insurance)">
                <mat-icon color="warn">delete</mat-icon>
                {{ 'common.delete' | translate }}
              </button>
            </ng-container>

            <ng-container *ngIf="insurance.carrierLookupId">
              <ng-container *appHasPermission="'setting.edit'">
                <mat-divider></mat-divider>

                <a
                  mat-menu-item
                  *mmxConsoleOnly
                  [routerLink]="['carrier', insurance.carrierLookupId]"
                  queryParamsHandling="preserve">
                  {{ 'patient-details.insurance-card.open-carrier-settings' | translate }}
                </a>
              </ng-container>
            </ng-container>
          </mat-menu>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.row-muted]="row.status === 'I'"
      ></tr>
    </table>
    <p
      *ngIf="readOnly && dataSource.data.length === 0 && otherDataSource.data.length === 0"
      class="insurances-card__empty"
      data-insurances-card-empty
    >
      {{ 'patient-details.insurance-card.empty' | translate }}
    </p>
  </mat-card-content>
</mat-card>
